import {RefObject, useEffect, useRef} from 'react';

/**
 * 官网 Hero 粒子动效 hook
 * - 在指定容器中绘制星点+鼠标连线动画
 * - CSS 要求：容器需 position: relative；canvas 需 absolute 覆盖
 */

const STAR_COUNT = 50;
const MOUSE_RADIUS = 280;
const STAR_LINK_RADIUS = 130;

interface Star {
    x: number;
    y: number;
    size: number;
    bright: number;
}

interface MouseState {
    x: number;
    y: number;
    active: boolean;
}

function genStars(): Star[] {
    const stars: Star[] = [];
    for (let i = 0; i < STAR_COUNT; i++) {
        let x: number, y: number, tries = 0;
        do {
            x = Math.random();
            y = Math.random();
            tries++;
        } while (tries < 50 && stars.some(s => Math.hypot(s.x - x, s.y - y) < 0.05));
        stars.push({x, y, size: 0.5 + Math.random() * 1.8, bright: 0.3 + Math.random() * 0.7});
    }
    return stars;
}

export function useHeroCanvas(
    sectionRef: RefObject<HTMLElement | null>,
    canvasRef: RefObject<HTMLCanvasElement | null>,
): void {
    const mouseRef = useRef<MouseState>({x: -9999, y: -9999, active: false});
    const starsRef = useRef<Star[]>([]);

    useEffect(() => {
        starsRef.current = genStars();
        const canvas = canvasRef.current;
        const section = sectionRef.current;
        if (!canvas || !section) return;

        let rafId: number;

        const resize = () => {
            const rect = section.getBoundingClientRect();
            const dpr = window.devicePixelRatio || 1;
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
            canvas.style.width = rect.width + 'px';
            canvas.style.height = rect.height + 'px';
        };
        resize();

        const onMove = (e: MouseEvent) => {
            const t = e.target as HTMLElement;
            const rect = section.getBoundingClientRect();
            mouseRef.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
                active: !t.closest('[data-hover-stop]'),
            };
        };
        const onLeave = () => {
            mouseRef.current.active = false;
        };

        section.addEventListener('mousemove', onMove);
        section.addEventListener('mouseleave', onLeave);

        const draw = () => {
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            const dpr = window.devicePixelRatio || 1;
            const w = canvas.width;
            const h = canvas.height;
            ctx.clearRect(0, 0, w, h);

            const {x: mx, y: my, active} = mouseRef.current;
            if (!active) {
                rafId = requestAnimationFrame(draw);
                return;
            }

            const mxs = mx * dpr;
            const mys = my * dpr;
            const connR = MOUSE_RADIUS * dpr;
            const linkR = STAR_LINK_RADIUS * dpr;
            const stars = starsRef.current;

            // Nearby stars
            const near = stars.map(s => ({
                ...s, px: s.x * w, py: s.y * h,
                dx: s.x * w - mxs, dy: s.y * h - mys,
                dist: 0,
            })).map(s => {
                s.dist = Math.hypot(s.dx, s.dy);
                return s;
            }).filter(s => s.dist < connR);

            // Star-to-star connections
            for (let i = 0; i < near.length; i++) {
                for (let j = i + 1; j < near.length; j++) {
                    const d = Math.hypot(near[i].px - near[j].px, near[i].py - near[j].py);
                    if (d >= linkR) continue;
                    const a = (1 - d / linkR) * 0.2;
                    ctx.beginPath();
                    ctx.moveTo(near[i].px, near[i].py);
                    ctx.lineTo(near[j].px, near[j].py);
                    ctx.strokeStyle = `rgba(255, 200, 100, ${a})`;
                    ctx.lineWidth = 0.5 * dpr;
                    ctx.stroke();
                }
            }

            // Mouse-to-star connections
            for (const s of near) {
                const a = (1 - s.dist / connR) * 0.35;
                ctx.beginPath();
                ctx.moveTo(mxs, mys);
                ctx.lineTo(s.px, s.py);
                ctx.strokeStyle = `rgba(255, 200, 100, ${a})`;
                ctx.lineWidth = 0.8 * dpr;
                ctx.stroke();
            }

            // Draw stars
            for (const s of near) {
                const a = (1 - s.dist / connR) * s.bright;
                ctx.beginPath();
                ctx.arc(s.px, s.py, s.size * dpr, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255, 220, 150, ${a})`;
                ctx.fill();
            }

            rafId = requestAnimationFrame(draw);
        };

        rafId = requestAnimationFrame(draw);

        const onResize = () => resize();
        window.addEventListener('resize', onResize);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', onResize);
            section.removeEventListener('mousemove', onMove);
            section.removeEventListener('mouseleave', onLeave);
        };
    }, [sectionRef, canvasRef]);
}
